import React from 'react'
import { useTranslation } from 'react-i18next'
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@mui/material'
import TradingStatsRow from './TradingStatsRow'
import { IssueEntity } from '../../../types/IssueEntity'

type TradingStatsOverviewProps = Pick<IssueEntity, 'currency' | 'tradesStats' | 'unitInstrumentSymbol'> & {
    sharesInUnit?: number | null
}

function TradingStatsOverview({
    currency,
    tradesStats,
    unitInstrumentSymbol,
    sharesInUnit,
}: TradingStatsOverviewProps) {
    const { t } = useTranslation()
    const hasShareSplitAdjustments = tradesStats.some((tradeData) => tradeData?.isSplitAdjusted)
    const hasPerShareAdjustedVwap = tradesStats.some(
        (tradeData) =>
            tradeData?.totalQuantity !== null && tradeData?.vwap !== null && typeof sharesInUnit === 'number',
    )

    return (
        <TableContainer>
            <Table>
                <TableHead>
                    <TableRow>
                        <TableCell>{t('pages.issues.tradingStats.timePoint')}</TableCell>
                        <TableCell>{t('pages.issues.tradingStats.vwap')}</TableCell>
                        <TableCell>{t('pages.issues.tradingStats.turnoverPerDay', { currency })}</TableCell>
                        <TableCell>{t('pages.issues.tradingStats.totalQuantity')}</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {tradesStats
                        .filter(({ periodType, numberOfDays }) => periodType === 'prePublication' && numberOfDays >= 10)
                        .map((stat) => (
                            <TradingStatsRow
                                key={stat.numberOfDays}
                                currency={currency}
                                row={stat}
                                timeLabel={t('pages.issues.tradingStats.daysBeforePublication', {
                                    days: stat.numberOfDays,
                                })}
                            />
                        ))}
                    {tradesStats
                        .filter(({ periodType }) => periodType === 'publicationToTerms')
                        .map((stat) => (
                            <TradingStatsRow
                                key={stat.periodType}
                                currency={currency}
                                row={stat}
                                timeLabel={t('pages.issues.tradingStats.betweenPublicationAndTerms')}
                            />
                        ))}
                    {tradesStats
                        .filter(({ periodType }) => periodType === 'subscriptionPeriod')
                        .map((stat) => (
                            <TradingStatsRow
                                key={stat.periodType}
                                currency={currency}
                                row={stat}
                                timeLabel={t('pages.issues.tradingStats.duringSubscriptionPeriod')}
                            />
                        ))}
                    {tradesStats
                        .filter(({ periodType }) => periodType === 'btuTradePeriod')
                        .map((stat) => (
                            <TradingStatsRow
                                key={stat.periodType}
                                currency={currency}
                                row={stat}
                                timeLabel={t('pages.issues.tradingStats.duringBtaBtuTrading')}
                            />
                        ))}
                    {tradesStats
                        .filter(({ periodType }) => periodType === 'postIpo')
                        .map((stat) => (
                            <TradingStatsRow
                                key={stat.periodType}
                                currency={currency}
                                row={stat}
                                timeLabel={
                                    stat.numberOfDays === 1
                                        ? t('pages.issues.tradingStats.firstTradingDay')
                                        : t('pages.issues.tradingStats.firstTradingDays', {
                                              days: stat.numberOfDays,
                                          })
                                }
                            />
                        ))}
                    {tradesStats
                        .filter(({ periodType }) => periodType === 'unitsTradePeriod')
                        .map((stat) => (
                            <TradingStatsRow
                                key={stat.periodType}
                                currency={currency}
                                row={stat}
                                timeLabel={t('pages.issues.tradingStats.unitTrading', {
                                    symbol: unitInstrumentSymbol,
                                })}
                                sharesInUnit={
                                    typeof sharesInUnit === 'number' && sharesInUnit > 1 ? sharesInUnit : null
                                }
                            />
                        ))}
                    {(hasShareSplitAdjustments || hasPerShareAdjustedVwap) && (
                        <TableRow>
                            {hasShareSplitAdjustments && (
                                <TableCell colSpan={hasPerShareAdjustedVwap ? 1 : 4}>
                                    <Typography sx={{ fontStyle: 'italic' }}>
                                        {t('pages.issues.tradingStats.shareSplitAdjustment')}
                                    </Typography>
                                </TableCell>
                            )}
                            {hasPerShareAdjustedVwap && (
                                <TableCell colSpan={hasShareSplitAdjustments ? 3 : 4}>
                                    <Typography align='right' sx={{ fontStyle: 'italic' }}>
                                        {t('pages.issues.tradingStats.unitsToSharesNote')}
                                    </Typography>
                                </TableCell>
                            )}
                        </TableRow>
                    )}
                </TableBody>
            </Table>
        </TableContainer>
    )
}

export default TradingStatsOverview
